<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="wrapper visible w-full">
    <!-- Left Panel: Application Selection -->
    <div class="application-selection">
      <!-- Search & Filter Header -->
      <div class="search-row sticky-header">
        <SmInput
          v-model="search"
          class="search-input"
          :label="i18n.t('search')"
          size="small"
          outline />
        <SmTooltip>
          <template #content>{{ i18n.t('showSelectedOnly') }}</template>
          <SmButton
            size="medium"
            type="primary"
            :outline="!showSelectedOnly"
            @click="showSelectedOnly = !showSelectedOnly">
            <Filter :size="16" />
          </SmButton>
        </SmTooltip>
      </div>

      <!-- Application Cards List -->
      <el-scrollbar class="scroll-container" max-height="100%">
        <div class="application-list">
          <!-- Application Card -->
          <div
            v-for="item in applicationFilter"
            :key="item._id"
            :class="[
              'application-selection-card',
              { active: isCardActive(item._id) },
            ]"
            :tabindex="0"
            role="button"
            :aria-pressed="isCardActive(item._id)"
            @click="() => handleClick(item)"
            @keydown.enter="() => handleClick(item)"
            @keydown.space.prevent="() => handleClick(item)">
            <!-- Icon Wrapper -->
            <div class="icon-wrapper">
              <ApplicationIcon
                :application-name="item.software_name"
                :size="40"
                :application-type="item.type" />
            </div>

            <!-- Information -->
            <div class="application-information">
              <span
                class="application-text"
                v-html="highlightSearchSubstring(item.display_name)" />

              <!-- License Count Badge -->
              <div class="license-info">
                <span
                  v-if="getSelectedLicense(item._id).length > 0"
                  class="count-badge">
                  {{ getSelectedLicense(item._id).length }}
                </span>
                <span
                  v-if="getSelectedLicense(item._id).length > 0"
                  class="license-count-text">
                  <SmTooltip>
                    <template #content>
                      <div class="flex flex-col gap-1">
                        <span
                          v-for="licenseName in getSelectedLicense(item._id)"
                          :key="licenseName">
                          {{ licenseName }}
                        </span>
                      </div>
                    </template>
                    {{ i18n.t('selected') }}
                  </SmTooltip>
                </span>
                <span v-else class="license-count-empty">
                  {{ i18n.t('licensesCount', 0) }} {{ i18n.t('selected') }}
                </span>
              </div>
            </div>

            <!-- Switch -->
            <div class="switch-wrapper">
              <SmTooltip>
                <template #content>
                  <span>{{
                    i18n.t('groupsCreateAccountTip', {
                      name: item.display_name,
                    })
                  }}</span>
                </template>
                <SmSwitch
                  v-model="activeApplication[item._id]"
                  @select="handleApplicationSelect(item._id)"
                  @unselect="handleApplicationUnselect(item._id)" />
              </SmTooltip>
            </div>
          </div>

          <!-- Empty State -->
          <div v-if="applicationFilter.length === 0" class="empty-state">
            <div class="empty-state-icon">
              <Search :size="48" class="text-contrast-muted" />
            </div>
            <p class="empty-state-text">
              {{ i18n.t('noApplicationsFound') }}
            </p>
          </div>
        </div>
      </el-scrollbar>
    </div>

    <!-- Right Panel: License Selection -->
    <div :class="['license-selection', 'visible']">
      <!-- Software Header with Icon -->
      <div v-if="currentlyEdited" class="license-header sticky-header">
        <div class="header-content">
          <!-- Software Icon -->
          <div class="header-icon">
            <ApplicationIcon
              :application-name="currentlyEdited.software_name"
              :size="48"
              :application-type="currentlyEdited.type" />
          </div>

          <!-- Title and Description -->
          <div class="header-text">
            <h2 class="header-title">
              {{ currentlyEdited.display_name }}
            </h2>
            <p v-if="currentlyEdited.desc" class="header-description">
              {{ currentlyEdited.desc }}
            </p>
          </div>

          <!-- Summary Badge -->
          <div
            v-if="getSelectedLicense(currentlyEdited._id).length > 0"
            class="header-badges">
            <span class="badge-label">{{ i18n.t('licensesSelected') }}</span>
            <span class="count-badge">
              {{ getSelectedLicense(currentlyEdited._id).length }}
            </span>
          </div>
        </div>
      </div>

      <!-- Empty State for Right Panel -->
      <div v-else class="license-empty-state">
        <div class="empty-icon">
          <LayoutGrid :size="64" class="text-contrast-muted" />
        </div>
        <p class="empty-text">
          {{ i18n.t('selectSoftwareToViewLicenses') }}
        </p>
      </div>

      <!-- License Toolbar -->
      <div v-if="currentlyEdited" class="license-toolbar sticky-header">
        <SmInput
          v-model="licenseSearch"
          class="license-search"
          :label="i18n.t('search')"
          size="small"
          outline />
        <SmTooltip>
          <template #content>
            <div class="tooltip-content">
              <p>{{ i18n.t('selectLicensesTooltip') }}</p>
              <p class="text-sm text-contrast-muted">
                {{ i18n.t('useCheckboxesHint') }}
              </p>
            </div>
          </template>
          <div class="license-helper">
            <Info :size="18" class="text-contrast-muted" />
          </div>
        </SmTooltip>
      </div>

      <!-- License List -->
      <el-scrollbar v-if="currentlyEdited" max-height="100%">
        <div class="spacer" />

        <!-- Sub-account Tabs -->
        <div
          v-if="
            Object.values(currentlyEdited.sub_accounts).length > 0 &&
            !currentlyEdited.sub_accounts['main']
          "
          class="tabs-wrapper sticky-tabs">
          <el-tabs v-model="selectedSubAccountId">
            <el-tab-pane
              v-for="sub_account in Object.values(currentlyEdited.sub_accounts)"
              :key="sub_account.id"
              :name="sub_account.id"
              :label="sub_account.name">
            </el-tab-pane>
          </el-tabs>
        </div>

        <!-- License Table -->
        <LicenseModelEditTable
          :key="currentlyEdited._id"
          ref="softwareLicenseEditTable"
          :software-licenses="currentlyEditedLicensesWithSubaccountFilter"
          :contracts="allContracts"
          :sub-account-id="selectedSubAccountId"
          :application="currentlyEdited"
          @selected-all="handleLicenseSelectedAll"
          @unselected-all="handleLicenseUnselectedAll"
          @selected="handleLicenseSelected"
          @unselected="handleLicenseUnselected"
          @close="currentlyEdited = null" />
      </el-scrollbar>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ComponentExposed } from 'vue-component-type-helpers'
  import { useI18n } from 'vue-i18n'
  import SmSwitch from '@/components/sm/SmSwitch.vue'
  import SmButton from '@/components/sm/SmButton.vue'

  import { PriceInformation, SoftwareLicense, SoftwareOut } from '@/client'

  const i18n = useI18n()
  const props = defineProps<{
    allSoftwareLicenses: SoftwareLicense[]
    allContracts: PriceInformation[]
  }>()

  import LicenseModelEditTable from '@/components/license/LicenseModelEditTable.vue'
  import { ModelRef } from 'vue'
  import { ApplicationStore } from '@/stores/application/application.store'
  import { Filter, Search, LayoutGrid, Info } from 'lucide-vue-next'

  const selectedSubAccountId = ref<string>('main')
  const softwareLicenseEditTable = ref<ComponentExposed<
    typeof LicenseModelEditTable
  > | null>(null)

  const currentlyEdited = ref<SoftwareOut | null>(null)

  type SoftwareLicenseID = string
  export interface SelectionState {
    [softwareId: string]: null | {
      [subAccountId: string]: Record<SoftwareLicenseID, SoftwareLicense | null>
    }
  }

  const selectionState: ModelRef<SelectionState> = defineModel('selection', {
    type: Object as PropType<SelectionState>,
    default: () => ({}),
  })

  const activeApplication = ref<Record<string, boolean>>({})

  const currentlyEditedLicenses = computed(() => {
    return props.allSoftwareLicenses.filter(
      (l) => l.software_id === currentlyEdited.value?._id
    )
  })

  const licenseSearch = ref('')

  const currentlyEditedLicensesWithSubaccountFilter = computed(() => {
    const licenses = currentlyEditedLicenses.value.filter(
      (license) =>
        license.sub_account_id === selectedSubAccountId.value ||
        license.sub_account_id == null
    )

    if (!licenseSearch.value) return licenses

    return licenses.filter((l) =>
      l.name.toLowerCase().includes(licenseSearch.value.toLowerCase())
    )
  })

  const { data: allApplications } =
    ApplicationStore.useApplications<SoftwareOut>()
  // #region Search
  const search = ref<string>('')
  const showSelectedOnly = ref(false)

  const applicationFilter = computed(() => {
    return allApplications.value
      .filter((item) => {
        const name = item.display_name || ''

        return name.toLowerCase().includes(search.value.toLowerCase())
      })
      .filter((item) => {
        // Hide SSO providers
        return !item.information.sso_provider
      })
      .filter((item) => {
        if (!showSelectedOnly.value) return true
        return activeApplication.value[item._id]
      })
      .sort((a, b) => {
        const nameA = a.display_name || ''
        const nameB = b.display_name || ''
        return nameA.localeCompare(nameB)
      })
  })

  // Default select first software so the right side dont look empty
  watch(allApplications, () => {
    currentlyEdited.value = applicationFilter.value[0]
  })

  function escapeHtml(text: string): string {
    const map: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
    }
    return text.replace(/[&<>"']/g, (m) => map[m])
  }

  function highlightSearchSubstring(text: string) {
    const escapedText = escapeHtml(text)
    if (!search.value) return escapedText

    const escapedSearch = escapeHtml(search.value).replace(
      /[.*+?^${}()|[\]\\]/g,
      '\\$&'
    )
    const regex = new RegExp(escapedSearch, 'gi')

    return escapedText.replace(regex, (match) => `<b>${match}</b>`)
  }
  // #endregion

  // #region License
  function applySelection() {
    if (!softwareLicenseEditTable.value) return
    if (!softwareLicenseEditTable.value.table) return // Should not happen
    const _selectedApplication =
      selectionState.value[currentlyEdited.value?._id || '']

    // Select the entry in the table for each actual selected license
    if (_selectedApplication == null) return
    Object.entries(
      _selectedApplication[selectedSubAccountId.value] || {}
    ).forEach(([id, value]) => {
      if (value != null) softwareLicenseEditTable.value?.table?.selectById(id)
    })
  }

  function handleLicenseSelectedAll(licenses: SoftwareLicense[]) {
    licenses.forEach((l) => handleLicenseSelected(l))
  }
  function handleLicenseUnselectedAll(licenses: SoftwareLicense[]) {
    licenses.forEach((l) => handleLicenseUnselected(l))
  }

  function handleLicenseSelected(license: SoftwareLicense) {
    if (!currentlyEdited.value) return

    const applicationId = currentlyEdited.value._id

    activeApplication.value[applicationId] = true

    if (!selectionState.value[applicationId])
      selectionState.value[applicationId] = {}

    if (!selectionState.value[applicationId]![selectedSubAccountId.value])
      selectionState.value[applicationId]![selectedSubAccountId.value] = {}
    selectionState.value[applicationId]![selectedSubAccountId.value][
      license._id
    ] = license
  }

  function handleLicenseUnselected(license: SoftwareLicense) {
    if (!currentlyEdited.value) return

    const applicationId = currentlyEdited.value._id

    if (!selectionState.value[applicationId])
      selectionState.value[applicationId] = {}

    if (!selectionState.value[applicationId]![selectedSubAccountId.value])
      selectionState.value[applicationId]![selectedSubAccountId.value] = {}

    selectionState.value[applicationId]![selectedSubAccountId.value][
      license._id
    ] = null
  }

  watch(
    [() => currentlyEdited.value, () => selectedSubAccountId.value],
    ([newApplication, newSubaccountId], [, oldSubaccountId]) => {
      if (!currentlyEdited.value) return

      // software Changes -> Select first sub_account of software by default
      if (newSubaccountId == oldSubaccountId) {
        if (Object.keys(currentlyEdited.value.sub_accounts).length > 0) {
          selectedSubAccountId.value = Object.keys(
            currentlyEdited.value.sub_accounts
          )[0]
        } else selectedSubAccountId.value = 'main'
      }

      // sub_account changes, de-select the table entries and re-select the right ones
      if (
        oldSubaccountId != newSubaccountId &&
        newApplication &&
        oldSubaccountId
      ) {
        if (!selectionState.value[newApplication._id]) {
          return
        }
        Object.keys(
          selectionState.value[newApplication._id]![oldSubaccountId] || {}
        ).forEach((id) => {
          softwareLicenseEditTable?.value?.table?.deselectById(id)
        })
      }

      nextTick(() => {
        applySelection()
      })
    }
  )

  // #endregion

  // #region Software
  function handleApplicationSelect(_id: string) {
    if (!selectionState.value[_id]) {
      selectionState.value[_id] = {}
    }
  }

  function handleApplicationUnselect(_id: string) {
    // Clear software selection and active state
    selectionState.value[_id] = null
    activeApplication.value[_id] = false

    // If the unselected app is currently being edited, clear the table selection
    if (currentlyEdited.value?._id === _id) {
      softwareLicenseEditTable.value?.table?.resetSelection()
    }
  }

  // #endregion

  // Utils
  function isCardActive(_id: string) {
    return currentlyEdited.value?._id === _id
  }

  function handleClick(item: SoftwareOut) {
    currentlyEdited.value = item
    applySelection()
  }

  function getSelectedLicense(softwareId: string) {
    const selectedLicense: string[] = []

    Object.keys(selectionState.value[softwareId] || {}).forEach(
      (subAccountId) => {
        if (!selectionState.value[softwareId]) return
        Object.values(selectionState.value[softwareId]![subAccountId]).forEach(
          (v) => {
            if (v != null) selectedLicense.push(v.name)
          }
        )
      }
    )
    return selectedLicense
  }

  watch(
    () => selectionState.value,
    (newState) => {
      // Update activeSoftware
      // This watcher ensures that the toggle switches in the left panel reflect the state of the internal selection map.
      // It handles the "ghost selection" issue where apps might appear selected incorrectly.
      Object.entries(newState).forEach(([id, val]) => {
        activeApplication.value[id] = val != null
      })
    },
    { deep: true, immediate: true }
  )
</script>

<style scoped lang="scss">
  // ==================== Layout ====================

  .wrapper {
    display: grid;
    grid-template-columns: 1fr 0fr;
    align-items: stretch;
    overflow: hidden;

    height: 55vh;

    transition: grid-template-columns 0.3s ease-in-out;

    &.visible {
      grid-template-columns: 1fr 1fr;
    }
  }

  // ==================== Left Panel: Application Selection ====================

  .application-selection {
    display: flex;
    flex-direction: column;
    height: 100%;
    max-height: 100%;
    min-width: 0;
    min-height: 0;
    overflow: hidden;
  }

  .search-row {
    display: flex;
    gap: 0.75rem;
    padding: 0.75rem 0.75rem 0.5rem;
    align-items: center;
    flex-shrink: 0;

    .search-input {
      flex: 1;
      min-width: 200px;
      margin-bottom: 0;
    }
  }

  .application-list {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
    padding: 0.5rem;
  }

  .scroll-container {
    flex: 1;
    overflow: hidden;
  }

  // ==================== Application Cards ====================

  .application-selection-card {
    display: flex;
    align-items: center;
    gap: 0.875rem;

    padding: 0.875rem;
    border-radius: var(--border-radius-base);
    background-color: var(--el-bg-color);
    border: 1.5px solid transparent;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

    // Hover state
    &:hover {
      background-color: var(--el-fill-color-light);
      box-shadow: var(--shadow-light);
      transform: translateY(-1px);
    }

    // Focus state for keyboard navigation
    &:focus-visible {
      outline: 2px solid var(--sm-primary);
      outline-offset: 2px;
    }

    // Active state
    &.active {
      border-color: var(--sm-primary);
      background-color: rgba(var(--sm-primary), 0.05);
      box-shadow: var(--shadow);

      .icon-wrapper {
        transform: scale(1.05);
      }

      .application-text {
        color: var(--sm-primary);
        font-weight: 600;
      }
    }
  }

  .icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: transform 0.2s ease-in-out;
  }

  .application-information {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .application-text {
    font-size: 1rem;
    font-weight: 500;
    color: var(--sm-contrast);
    line-height: 1.4;
    word-break: break-word;
  }

  .license-info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .count-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 18px;
    min-width: 18px;
    padding: 0 5px;
    border-radius: 9px;
    background-color: var(--sm-primary);
    color: white;
    font-size: 0.75rem;
    font-weight: 600;
    line-height: 1;
  }

  .license-count-text {
    font-size: 0.8125rem;
    color: var(--sm-contrast-muted);
    cursor: help;
  }

  .license-count-empty {
    font-size: 0.8125rem;
    color: var(--el-text-color-secondary);
  }

  .switch-wrapper {
    flex-shrink: 0;
    margin-left: 0.5rem;
  }

  // ==================== Empty State ====================

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
    text-align: center;
  }

  .empty-state-icon {
    margin-bottom: 1rem;
    opacity: 0.5;
  }

  .empty-state-text {
    color: var(--el-text-color-secondary);
    font-size: 0.9375rem;
    margin: 0;
  }

  // ==================== Right Panel: License Selection ====================

  .license-selection {
    min-width: 0;
    min-height: 0;
    overflow: hidden;

    display: flex;
    flex-direction: column;
    gap: 0.75rem;

    padding: 0;
    margin: 0;

    transition: all 0.3s ease-in-out;

    max-height: 100%;

    &.visible {
      border-left: 1px solid var(--el-border-color-light);
      padding-left: 1rem;
    }
  }

  // ==================== License Header ====================

  .license-header {
    padding: 0.5rem 0;
    flex-shrink: 0;
  }

  .header-content {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    padding: 1rem;
    background-color: var(--el-fill-color-blank);
    border: 1px solid var(--el-border-color-light);
    border-radius: var(--border-radius-base);
  }

  .header-icon {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    background-color: var(--el-fill-color-light);
    border-radius: var(--border-radius-base);
  }

  .header-text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }

  .header-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--sm-contrast);
    margin: 0;
    line-height: 1.4;
  }

  .header-description {
    font-size: 0.875rem;
    color: var(--sm-contrast-muted);
    margin: 0;
    line-height: 1.5;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .header-badges {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .header-badges .badge-label {
    font-size: 0.8125rem;
    color: var(--sm-contrast-muted);
    white-space: nowrap;
  }

  // ==================== License Empty State ====================

  .license-empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    padding: 4rem 2rem;
    text-align: center;
    color: var(--el-text-color-secondary);
  }

  .license-empty-state .empty-icon {
    opacity: 0.4;
    margin-bottom: 1rem;
  }

  .license-empty-state .empty-text {
    font-size: 0.9375rem;
    margin: 0;
  }

  // ==================== License Toolbar ====================

  .license-toolbar {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-shrink: 0;
  }

  .license-search {
    flex: 1;
    min-width: 200px;
    margin-bottom: 0;
  }

  .license-helper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: var(--border-radius-small);
    background-color: var(--el-fill-color-light);
    cursor: help;
    transition: all 0.2s ease-in-out;

    &:hover {
      background-color: var(--el-fill-color);
      color: var(--sm-primary);
    }
  }

  .tooltip-content {
    p {
      margin: 0;
      line-height: 1.5;

      &:not(:last-child) {
        margin-bottom: 0.5rem;
      }
    }
  }

  // ==================== Tabs ====================

  .tabs-wrapper {
    flex-shrink: 0;
  }

  .sticky-tabs {
    position: sticky;
    top: 60px;
    background-color: var(--el-bg-color);
    z-index: 9;
    padding-bottom: 0.5rem;
  }

  // ==================== Common ====================

  .sticky-header {
    position: sticky;
    top: 0;
    background-color: var(--el-bg-color);
    z-index: 10;
    padding-bottom: 0.5rem;
  }

  .spacer {
    height: 0.5rem;
    flex-shrink: 0;
  }

  // ==================== Dark Mode Adjustments ====================

  .dark {
    .application-selection-card:hover {
      background-color: var(--el-fill-color-dark);
    }

    .application-selection-card.active {
      background-color: rgba(var(--sm-primary), 0.08);
    }

    .header-content {
      background-color: var(--el-fill-color-light-dark);
      border-color: var(--el-border-color-darker);
    }

    .license-helper {
      background-color: var(--el-fill-color-dark);

      &:hover {
        background-color: var(--el-fill-color);
      }
    }
  }
</style>
